import {
    type FC,
    type ReactNode,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useMediaQuery } from "@onshopfront/components";
import { isRecord, type PartialRequired } from "../../../types/internal/utility";
import { useShopfrontDispatch, useShopfrontSelector } from "../../_Bootstrap/hooks";
import { store } from "../../_Bootstrap/RoutingAndStore";
import { showDialog } from "../../Actions/Dialogs/DialogActions";
import Button from "../../Common/Buttons/Button";
import LoadingScreen from "../../Common/Loading/LoadingScreen";
import PopupManager from "../../Common/Popups/PopupManager";
import { determineDataSource } from "../../Data/DataStores/DataSource";
import { type ReportClauses } from "../../Utilities/Domain/Reports/ReportParser";
import { usePermissions } from "../../Utilities/Domain/Users/Permissions";
import FontAwesomeLight from "../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import { parseReport } from "../../Utilities/Platform/Reports";
import { CacheRequest } from "../../Utilities/Platform/Request";
import { Subscription } from "../../Utilities/Platform/Subscription/Subscription";
import { print, PrintWrapper } from "../../Utilities/UI/PrintHelper";
import { type DashboardAction, DashboardActions } from "./DashboardActions";
import { DashboardManager } from "./DashboardManager";
import { DashboardUserSelect } from "./DashboardUserSelect";
import { DashboardItem } from "./Item/DashboardItem";
import { DashboardModify } from "./Modify/DashboardModify";

export interface DashboardItemType {
    report: string;
    position: {
        x: number;
        y: number;
        w: number;
        h: number;
    };
    filterUser: boolean;
}

export interface DashboardReport {
    id: string;
    name: string;
    type: string;
    report: ReportClauses;
}

export const DASHBOARD_ROW_HEIGHT = 150;

function isDashboardItem(data: unknown): data is PartialRequired<DashboardItemType, "report" | "position"> {
    if(!isRecord(data)) {
        return false;
    }

    if(!("position" in data)) {
        return false;
    }

    const position = data.position;

    if(!isRecord(position)) {
        return false;
    }

    if(
        !(
            "x" in position && typeof position.x === "number" &&
            "y" in position && typeof position.y === "number" &&
            "w" in position && typeof position.w === "number" &&
            "h" in position && typeof position.h === "number"
        )
    ) {
        return false;
    }

    return "report" in data && typeof data.report === "string";
}

function parseDashboard(dashboard: unknown): Array<DashboardItemType> {
    if(typeof dashboard !== "string") {
        return [];
    }

    try {
        const parsed = JSON.parse(dashboard);

        if(!Array.isArray(parsed)) {
            return [];
        }

        const items: Array<DashboardItemType> = [];

        for(const item of parsed) {
            if(!isDashboardItem(item)) {
                continue;
            }

            items.push({
                position  : item.position,
                report    : item.report,
                filterUser: item.filterUser ?? true,
            });
        }

        return items;
    } catch(e) {
        console.error("Unable to parse the dashboard", e);
        PopupManager.error("Error loading the user dashboard");

        return [];
    }
}

const permissions = [ "edit_user_dashboard" ] as const;

export const Dashboard: FC = () => {
    const { editUserDashboard } = usePermissions(permissions);

    const dashboardManager = useMemo(() => {
        return new DashboardManager();
    }, []);

    const isAdmin = useShopfrontSelector(state => {
        if(state.user.id) {
            return state.user.isAdmin ?? false;
        }

        return false;
    });

    const [ user, setUser ] = useState<null | string>(() => {
        const state = store.getState();

        return state.user.id ? state.user.id : null;
    });

    const canEdit = useMemo(() => {
        return Subscription.can(Subscription.Features.USER_DASHBOARD) && editUserDashboard;
    }, [ editUserDashboard ]);

    const dispatch = useShopfrontDispatch();

    const [ loading, setLoading ] = useState(user !== null);
    const [ editing, setEditing ] = useState(false);
    const [ reports, setReports ] = useState<Array<DashboardReport>>([]);
    const [ items, setItems ] = useState<Array<DashboardItemType>>([]);

    const smallScreen = useMediaQuery("(max-width: 864px)");

    useEffect(() => {
        if(!user) {
            return;
        }

        setLoading(true);

        Promise.all([
            CacheRequest.get<Record<string, {
                name: string;
                query: string;
                type: string;
            }>>("/dashboard/reports", {
                params: {
                    user, // This is here to stop caching for other users, not actually used in request
                },
            }),
            determineDataSource().then(source => source.users.get(user)),
        ])
            .then(([ response, user ]) => {
                const dataReports = response.data;

                const items = parseDashboard(user.dashboard);

                const reports: Array<DashboardReport> = [];

                for(const id in dataReports) {
                    if(!Object.prototype.hasOwnProperty.call(dataReports, id)) {
                        continue;
                    }

                    const report = dataReports[id];

                    try {
                        reports.push({
                            id,
                            name  : report.name,
                            type  : report.type,
                            report: parseReport(report.query),
                        });
                    } catch(err) {
                        console.error("Error parsing report", err);
                    }
                }

                for(const item of items) {
                    const report = reports.find(report => report.id === item.report);

                    if(!report) {
                        continue;
                    }

                    dashboardManager.preloadReport(user.uuid, report, item.filterUser);
                }

                setLoading(false);
                setReports(reports);
                setItems(items);
            })
            .catch(err => {
                console.error("Error loading user dashboard", err);
                PopupManager.error("Failed to load the user dashboard");

                setLoading(false);
                setReports([]);
                setItems([]);
            });
    }, [ user, dashboardManager ]);

    if(loading) {
        return (
            <LoadingScreen>
                Loading User Dashboard...
            </LoadingScreen>
        );
    }

    const onEdit = () => {
        setEditing(true);
    };

    let content: ReactNode;

    if(editing && canEdit && user) {
        content = (
            <DashboardModify
                smallScreen={smallScreen}
                user={user}
                items={items}
                onCancel={() => {
                    setEditing(false);
                }}
                onSave={(items, reports) => {
                    setItems(items);
                    setReports(reports);
                    setEditing(false);
                }}
            />
        );
    } else {
        const actions: Array<DashboardAction> = [];

        if(isAdmin) {
            actions.push({
                classes: "bg-neutral-700",
                icon   : "users",
                label  : "Users",
                onClick: (e) => {
                    e.stopPropagation();

                    dispatch(showDialog(
                        "Select User",
                        <DashboardUserSelect
                            user={user}
                            onSelect={user => {
                                setUser(user);
                            }}
                        />,
                        "question"
                    ));
                },
            });
        }

        if(canEdit && user && !smallScreen) {
            actions.push({
                classes: "bg-green-600",
                icon   : "pencil",
                label  : "Edit",
                onClick: onEdit,
            });
        }

        actions.push({
            disabled: !items.length,
            classes : "bg-neutral-700",
            icon    : "print",
            label   : "Print",
            onClick : () => {
                print();
            },
        });

        let dashboard: ReactNode;

        if(!user) {
            dashboard = (
                <div className="flex justify-center items-center text-3xl h-full">
                    <FontAwesomeLight icon="users" className="opacity-80 mr-4" />
                    Select a user to view and edit their dashboard
                </div>
            );
        } else if(items.length) {
            dashboard = (
                <div
                    className={smallScreen ? "" : "grid grid-cols-10 min-h-full"}
                    style={{
                        gridAutoRows: `${DASHBOARD_ROW_HEIGHT}px`,
                    }}
                >
                    {items.map((item, index) => {
                        const report = reports.find(report => report.id === item.report);

                        return (
                            <DashboardItem
                                key={index}
                                user={user}
                                item={item}
                                report={report ?? null}
                                manager={dashboardManager}
                                smallScreen={smallScreen}
                            />
                        );
                    })}
                </div>
            );
        } else {
            dashboard = (
                <div className="flex flex-col justify-center items-center h-full">
                    <div className="text-4xl mb-4 text-neutral-500 dark:text-dark-600">No User Dashboard</div>
                    {canEdit && (
                        <div className="text-lg">
                            <Button
                                classes={[ "button-success" ]}
                                onClick={onEdit}
                            >
                                <FontAwesomeLight icon="pencil" />Edit
                            </Button>
                        </div>
                    )}
                </div>
            );
        }

        content = (
            <>
                {dashboard}
                <DashboardActions
                    actions={actions}
                />
            </>
        );
    }

    return (
        <PrintWrapper>
            <div className="relative w-full h-full p-4 overflow-auto">
                {content}
            </div>
        </PrintWrapper>
    );
};
